import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';

import {ErrorBoundary} from '@sentry/react';
import {AccountInformation} from 'src/components/account-information';
import {useValidateAccountDetails} from 'src/components/account-information/hooks/useValidateAccountDetails';
import {ProfileMain} from 'src/components/profile-tab-structure/profile-main';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContext} from 'src/contexts/validation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {BASIC_VALIDATION_FAILED} from 'src/modules/create-account/constants';
import {DEFAULT_DETAILS} from 'src/modules/create-account/steps/account-details/account-details';
import {clearAccountProfileTab} from 'src/modules/profile/account/tabs/account-profile/services/account-profile-slice';
import {getAccountHierarchyTree} from 'src/openapi/admin/apis/AccountHierarchyApi';
import {updateAccount} from 'src/openapi/admin/apis/UpdateAccountApi';
import {updateAccountCrmId} from 'src/openapi/admin/apis/UpdateAccountCrmIdApi';
import {updateAccountPbIds} from 'src/openapi/admin/apis/UpdateAccountPbIdsApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import type {MessageContextType} from 'src/contexts/message-context';
import type {ValidationContextType} from 'src/contexts/validation-context';
import type {AccountDetails} from 'src/modules/create-account/typedef';

type Props = {
    accountId: number,
};

const AccountDetailsTab: React.FC<Props> = ({accountId}) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const {accountSubmit, accountFieldChange} = useEventTracking();

    const [initialAccountDetails, setInitialAccountDetails] = useState<AccountDetails>(DEFAULT_DETAILS);
    const [accountDetails, setAccountDetails] = useState<AccountDetails>(DEFAULT_DETAILS);
    const [busy, setBusy] = useState(false);

    const {validationErrors} = useContext<ValidationContextType>(ValidationContext);
    const {setSuccessMessage, setErrorMessage} = useContext<MessageContextType>(MessageContext);

    const {validateUnfilled, clearErrors} = useValidateAccountDetails();
    const {canEditAccount} = useAdminAccessRight();

    const {isCurrentChunkChanged, proceed, setIsChunkChanged} = useConfirmationChunk({
        chunkName: 'account-details',
        onProceed: () => setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE),
    });

    useEffect(() => {
        const changed = !isDeepEqual({prev: initialAccountDetails, current: accountDetails});
        setIsChunkChanged(changed);
    }, [initialAccountDetails, accountDetails, setIsChunkChanged]);

    const onLoad = useCallback((details: AccountDetails) => {
        setInitialAccountDetails(details);
        setAccountDetails(details);
    }, []);

    const onSave = useCallback(async () => {
        accountSubmit({place: 'Account Details', submit: true});

        const hasErrors = !validateUnfilled(accountDetails) || !!validationErrors.length;

        if (hasErrors) {
            setErrorMessage(BASIC_VALIDATION_FAILED);
            return;
        }

        const {accountName, countryCode, statusCode, entityTypeCode, templateUser} = accountDetails;
        let responseErrors: Array<string> = [];

        if (accountName && countryCode && statusCode && entityTypeCode && templateUser) {
            setBusy(true);
            const updateAccountResponse = await apiDispatch(updateAccount({
                accountUpdateRequest: {
                    accountName,
                    countryCode,
                    entityTypeCode,
                    statusCode,
                    templateUserId: templateUser.userId,
                    parentAccountId: accountDetails.parentAccount?.accountId || undefined,
                },
                accountId,
            }));

            if (updateAccountResponse.payload.error) {
                responseErrors = responseErrors.concat(getError({
                    error: updateAccountResponse.payload.data,
                    separator: ' ',
                }));
            } else {
                await apiDispatch(getAccountHierarchyTree({accountId}));
            }

            const updateSFDCIDResponse = await apiDispatch(updateAccountCrmId({
                accountId,
                body: accountDetails.sfdcId || '',
            }));

            if (updateSFDCIDResponse.payload.error) {
                responseErrors = responseErrors.concat(
                    getError({error: updateSFDCIDResponse.payload.data, separator: ' '}),
                );
            }

            const updatePBIDsResponse = await apiDispatch(updateAccountPbIds({
                accountId,
                requestBody: accountDetails.pbIds.filter(Boolean),
            }));
            if (updatePBIDsResponse.payload.error) {
                responseErrors = responseErrors.concat(
                    getError({error: updatePBIDsResponse.payload.data, separator: ' '}),
                );
            }

            setBusy(false);
            if (!responseErrors.length) {
                setInitialAccountDetails(accountDetails);
                proceed();
            } else {
                setErrorMessage(responseErrors.join('\n'));
            }
            dispatch(clearAccountProfileTab());
        }
    }, [accountDetails,
        accountId,
        validationErrors,
        accountSubmit,
        apiDispatch,
        dispatch,
        proceed,
        setErrorMessage,
        validateUnfilled,
    ]);

    const onDiscard = useCallback(() => {
        accountSubmit({place: 'Account Details', submit: false});
        setAccountDetails(initialAccountDetails);
        clearErrors();
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [initialAccountDetails, clearErrors, accountSubmit, setSuccessMessage]);

    const onFieldChange = useCallback((parameterName: string) => {
        accountFieldChange({
            section: 'Account Information',
            viewType: 'Existed',
            parameterName,
        });
    }, [accountFieldChange]);

    return (
        <ErrorBoundary>
            <ProfileMain header={(
                <Item grow={1}>
                    <Group justify="space-between">
                        <Typography
                            fontWeight="semi-bold"
                            variant="heading-level-3">
                            Account Details
                        </Typography>
                        {canEditAccount(accountId) && (
                            <Group>
                                <Button
                                    variant="Type.SECONDARY"
                                    onClick={onDiscard}
                                    disabled={!isCurrentChunkChanged}>
                                    Discard Changes
                                </Button>
                                <Button
                                    busy={busy}
                                    variant="Type.PRIMARY"
                                    disabled={!isCurrentChunkChanged}
                                    onClick={onSave}>
                                    Save Changes
                                </Button>
                            </Group>
                        )}
                    </Group>
                </Item>
            )}>
                <AccountInformation {...{
                    accountId,
                    accountDetails,
                    onLoad,
                    onChange: setAccountDetails,
                    editDisabled: !canEditAccount(accountId),
                    onFieldChange,
                }}
                />
            </ProfileMain>
        </ErrorBoundary>
    );
};

export const ValidatedAccountDetails: React.FC<Props> = ({accountId}) => (
    <ValidationContextProvider>
        <AccountDetailsTab accountId={accountId}/>
    </ValidationContextProvider>
);
